import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'

import {
    DialogContent, DialogTitle, Stack, Typography, Card, CardContent,
    Grid, InputLabel, IconButton, Tooltip, Dialog, Divider, Box, CircularProgress
} from '@mui/material'
import { EditOutlined, CloseOutlined, FileTextOutlined } from '@ant-design/icons'

// project import
import StringAvatar from 'components/common/StringAvatar'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { getPermissionKeys } from 'utils/util'
import { EVENT_CATEGORY_DETAIL_INIT, EVENT_CATEGORY_UPDATE_INIT, EVENT_CATEGORY_UPDATE_SUCCESS } from 'models/events/category.event'
import { categoryDetail } from 'models/category'
import { getImageUrl, formatDate } from 'utils/helpers'
import { useApiData } from 'services/hook/useApiData'


/*
 * Initiate Category detail popup
 */
export const CategoryDetailDialog = ({ isEdit = true }) => {
    const [categoryId, setCategoryId] = useState(null)
    const eventbus = useEventBusContext()

    useEffect(() => {
        return eventbus.on(EVENT_CATEGORY_DETAIL_INIT, (data) => {
            setCategoryId(data.id) 
        })
    }, [eventbus])

    return (
        <Dialog open={categoryId != null} maxWidth="md" fullWidth scroll={'body'} onClose={() => setCategoryId(null)} >
            {categoryId && <CategoryDetail categoryId={categoryId} isEdit={isEdit} onClose={() => setCategoryId(null)} />}
        </Dialog>
    )
}


/*
 * Category Detail Content
 */
const CategoryDetail = ({ categoryId, isEdit, onClose }) => {
    const eventbus = useEventBusContext()
    const permissionsKeys = getPermissionKeys('category')

    const [data, , loading, loadData] = useApiData({ data: {} }, {}, (params, setData) => {
        if (!categoryId) {
            setData({})
            return
        }
        categoryDetail(categoryId).then((res) => {
            setData(res.data || res)
        })
        .catch((res) => {
            console.error('Failed to load category details:', res)
            setData({})
        })
    })

    // Reload data when category is updated
    useEffect(() => {
        return eventbus.on(EVENT_CATEGORY_UPDATE_SUCCESS, () => {
            loadData()
        })
    }, [eventbus, loadData])

    // Load data on mount
    useEffect(() => {
        if (categoryId) {
            loadData()
        }
    }, [categoryId, loadData])

    const InfoRow = ({ label, value }) => (
        <Grid item size={{ xs:12, md:6 }} >
            <Stack spacing={1}>
                <InputLabel sx={{ fontWeight: 600, mb: 0 }}>{label}</InputLabel>
                <Typography variant="body1" sx={{textTransform: 'capitalize'}}> {value || '-'} </Typography>
            </Stack>
        </Grid>
    )

    if (loading) {
        return (
            <>
                <DialogTitle>Loading...</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </>
        )
    }

    if (!data || !data.name) {
        return (
            <>
                <DialogTitle>Category Not Found</DialogTitle>
                <DialogContent>
                    <Typography color="error">Unable to load category details.</Typography>
                </DialogContent>
            </>
        )
    }

    return (
        <>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ flexGrow: 1 }}>
                        <StringAvatar text={data.name} size="large" />
                        <Box>
                            <Typography variant="h4">{data.name}</Typography>
                            <Typography variant="body2" color="textSecondary"> 
                                Status: {data.status} 
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        {permissionsKeys.can_update && isEdit && (
                            <Tooltip title="Edit Category">
                                <IconButton color="primary"
                                    onClick={() => {
                                        onClose()
                                        eventbus.fire(EVENT_CATEGORY_UPDATE_INIT, categoryId)
                                    }} 
                                    size="large"
                                >
                                    <EditOutlined />
                                </IconButton>
                            </Tooltip>
                        )}

                        <IconButton onClick={onClose} size="large"> 
                            <CloseOutlined /> 
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent>
                <Stack spacing={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}> 
                                Category Information 
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item size={{ xs:12, md:4 }} >
                                    <Stack spacing={2}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            bgcolor: 'grey.50', 
                                            borderRadius: 2, 
                                            p: 3, 
                                            minHeight: 140 
                                        }}>
                                            {data.image_url ? (
                                                <Box 
                                                    component="img" 
                                                    src={getImageUrl(data.image_url)} 
                                                    alt={data.name} 
                                                    sx={{ 
                                                        maxWidth: '100%', 
                                                        maxHeight: 140, 
                                                        objectFit: 'contain' 
                                                    }} 
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No Image
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Grid>

                                <Grid item size={{ xs:12, md:8 }} >
                                    <Grid container spacing={3}>
                                        <InfoRow label="Name" value={data.name} />
                                        <InfoRow label="Status" value={data.status} />
                                        <InfoRow label="Products Count" value={data.products_count || 0} />
                                        <InfoRow label="Created At" value={formatDate(data.created_at)} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {data.description && (
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                                    <FileTextOutlined style={{ marginRight: 8 }} />
                                    Description
                                </Typography>
                                <Typography variant="body1" sx={{ 
                                    whiteSpace: 'pre-wrap', 
                                    backgroundColor: 'background.default', 
                                    p: 2, 
                                    borderRadius: 1 
                                }}>
                                    {data.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Stack>
            </DialogContent>
        </>
    )
}

CategoryDetail.propTypes = {
    categoryId: PropTypes.number.isRequired,
    isEdit: PropTypes.bool,
    onClose: PropTypes.func.isRequired
}

export default CategoryDetail